import { action, computed, observable } from 'mobx';
import { RootStore } from './rootStore';
import {
  API_ACCOUNT_VARIANT,
  API_ACCOUNT_EDIT_BASIC,
  API_ACCOUNT_ACCOUNTS,
  API_ACCOUNT_LINK_ACCOUNT,
} from 'api/endpoints';
import { Currency, AccountMode, AccountStatus } from 'enums';
import { AccountEditBasic } from 'stores/workflowStores/workflowBasicStore/model';

import { LinkableAccount } from 'pages/Accounts/Edit/components/ExternalProducts/model';

interface Variant {
  mode: AccountMode;
}

export class EditAccountStore {
  private rootStore: RootStore;

  @observable privateStep = 0;
  @observable accountStatus: null | AccountStatus = null;
  @observable data: AccountEditBasic | null = null;
  @observable accountId = '';
  @observable mode: AccountMode | null = null;
  @observable accounts: LinkableAccount[] | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @action
  fetchBasicData = async (accountId: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    this.data = null;
    this.accountId = accountId;
    await this.fetchVariant();
    const response = await this.rootStore.stores.apiStore.get<AccountEditBasic>(API_ACCOUNT_EDIT_BASIC(accountId));
    if (response?.data) {
      this.data = response.data;
      this.accountStatus = this.data.accountStatus || null;
      this.rootStore.stores.breadcrumbsStore.updateByTitle(accountId, this.accountName);
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  fetchVariant = async () => {
    const response = await this.rootStore.stores.apiStore.get<Variant>(API_ACCOUNT_VARIANT(this.accountId));
    if (response?.data) {
      this.mode = response.data.mode;
    }
  };

  @action
  setVariant = async (mode: AccountMode): Promise<boolean> => {
    const response = await this.rootStore.stores.apiStore.post(API_ACCOUNT_VARIANT(this.accountId), { mode });
    if (response?.status === 200) {
      this.mode = mode;
      return true;
    } else {
      return false;
    }
  };

  @action
  getAccounts = async () => {
    const response = await this.rootStore.stores.apiStore.get<LinkableAccount[]>(API_ACCOUNT_ACCOUNTS(this.accountId), [400]);
    if (response?.data) {
      this.accounts = response.data;
    }
  };

  @action
  linkAccount = async (accountId: string) => {
    const response = await this.rootStore.stores.apiStore.post(API_ACCOUNT_LINK_ACCOUNT(this.accountId), { accountId });
    return !!response?.data;
  };

  @computed
  get currency(): Currency {
    return this.data?.currency || Currency.NONE;
  }

  @computed
  get accountName(): string {
    return this.data?.title || '';
  }

  @action
  setStep = (step: number): void => {
    this.privateStep = step;
  };

  @computed
  get step(): number {
    return this.privateStep;
  }

  @computed
  get isDraft(): boolean {
    return this.accountStatus === AccountStatus.DRAFT;
  }
}
